import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Table, Badge, Alert, Spinner, Form, Button } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
ChartJS.register(ArcElement, ChartTooltip, Legend);
import axios from 'axios';

const number = (v) => (Number(v || 0)).toFixed(2);

const getPaymentProviderText = (provider) => {
  const providerMap = {
    'expresspay': 'ExpressPay',
    'cash': 'Наличные',
    'card': 'Карта',
    'onsite': 'На месте'
  };
  return providerMap[provider] || provider || '—';
};

const getPaymentStatusText = (status) => {
  const statusMap = {
    'completed': 'Завершен',
    'accepted': 'Принят',
    'pending': 'Ожидает',
    'rejected': 'Отклонен'
  };
  return statusMap[status] || status || '—';
};

const OwnerStats = () => {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const ownerToken = useMemo(() => localStorage.getItem('restaurantToken') || '', []);
  const managerToken = useMemo(() => localStorage.getItem('managerToken') || '', []);
  const token = ownerToken || managerToken;

  const load = async (range) => {
    try {
      setLoading(true);
      setError('');
      const profileUrl = ownerToken ? '/api/auth/profile' : '/api/manager/profile';
      const me = await axios.get(profileUrl, { headers: { 'x-auth-token': token } });
      setProfile(me.data);
      
      const rid = ownerToken
        ? (me.data?._id)
        : (me.data?.restaurantId?._id || me.data?.restaurantId);
        
      const params = new URLSearchParams();
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
      const apiBase = (process.env.REACT_APP_BACKEND_BASE_URL || '').replace(/\/$/, '') || '';
      const url = `${apiBase}/api/orders/restaurant/${rid}/stats?${params.toString()}`;
      
      const res = await axios.get(url, { headers: { 'x-auth-token': token } });
      setStats(res.data);
    } catch (e) {
      console.error('📊 Stats error:', e);
      setError(e.response?.data?.message || e.message || 'Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        await load();
      } catch (e) {
        // handled in load
      } finally {
        // noop
      }
    };
    if (token) init();
  }, [token]);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  if (!token) {
    return (
      <Container className="mt-5"><Alert variant="warning">Нет токена. Войдите как владелец/менеджер.</Alert></Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-5 text-center"><Spinner animation="border" size="sm" /> Загрузка...</Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5"><Alert variant="danger">{error}</Alert></Container>
    );
  }

  const totals = stats?.totals || { orders: 0, revenue: 0, avgCheck: 0, completedOrders: 0 };
  const byDay = stats?.byDay || [];
  const byType = stats?.byType || [];
  const byPayment = stats?.byPayment || [];
  const topItems = stats?.topItems || [];

  return (
    <Container className="mt-4">
      <Row className="mb-3">
        <Col><h1>Статистика (30 дней)</h1><div className="text-muted">{profile?.restaurantName || ''}</div></Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card><Card.Body>
            <div className="text-muted">Выручка (завершенные)</div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>{number(totals.revenue)} BYN</div>
          </Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card><Card.Body>
            <div className="text-muted">Всего заказов</div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>{totals.orders}</div>
          </Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card><Card.Body>
            <div className="text-muted">Завершенные заказы</div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>{totals.completedOrders}</div>
          </Card.Body></Card>
        </Col>
        <Col md={3}>
          <Card><Card.Body>
            <div className="text-muted">Средний чек (завершенные)</div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>{number(totals.avgCheck)} BYN</div>
          </Card.Body></Card>
        </Col>
      </Row>

      <Card className="mb-3"><Card.Body>
        <Form className="row g-2 align-items-end">
          <Col md={3}>
            <Form.Group>
              <Form.Label>С даты</Form.Label>
              <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>По дату</Form.Label>
              <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Button variant="outline-primary" onClick={() => load({ from, to })}>Показать</Button>
          </Col>
          <Col md={3} className="text-md-end">
            <Button variant="outline-secondary" onClick={() => { setFrom(''); setTo(''); load(); }}>Сбросить</Button>
          </Col>
        </Form>
      </Card.Body></Card>

      <Row className="g-3">
        <Col md={6}>
          <Card className="mb-3"><Card.Body>
            <Card.Title>Динамика по дням</Card.Title>
            {byDay.length === 0 ? <div className="text-muted">Нет данных</div> : (
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={byDay.map(d => ({ date: d._id, revenue: Number(d.revenue), orders: d.orders }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={Math.ceil(byDay.length / 7)} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#0d6efd" dot={false} name="Выручка" />
                    <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#198754" dot={false} name="Заказы" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card.Body></Card>

          <Card className="mb-3"><Card.Body>
            <Card.Title>Методы/Статусы оплаты</Card.Title>
            {byPayment.length === 0 ? <div className="text-muted">Нет данных</div> : (
              <Table size="sm" striped hover responsive>
                <thead><tr><th>Тип оплаты</th><th>Статус</th><th className="text-end">Заказы</th><th className="text-end">Выручка</th></tr></thead>
                <tbody>
                  {byPayment.map((p, idx) => (
                    <tr key={idx}>
                      <td>{getPaymentProviderText(p._id?.provider)}</td>
                      <td>{p._id?.status ? <Badge bg={
                        p._id.status === 'completed' ? 'success' : 
                        (p._id.status === 'pending' ? 'warning' : 
                        (p._id.status === 'rejected' ? 'danger' : 'secondary'))
                      }>{getPaymentStatusText(p._id.status)}</Badge> : '—'}</td>
                      <td className="text-end">{p.orders}</td>
                      <td className="text-end">{number(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body></Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3"><Card.Body>
            <Card.Title>Каналы продаж</Card.Title>
            {byType.length === 0 ? <div className="text-muted">Нет данных</div> : (
              <Pie data={{
                labels: byType.map(t => (t._id === 'dine_in' ? 'В зале' : 'Доставка')),
                datasets: [{
                  label: 'Выручка',
                  data: byType.map(t => Number(t.revenue)),
                  backgroundColor: ['#0d6efd88', '#19875488'],
                  borderColor: ['#0d6efd', '#198754'],
                  borderWidth: 1
                }]
              }} />
            )}
          </Card.Body></Card>

          <Card><Card.Body>
            <Card.Title>Топ блюд</Card.Title>
            {topItems.length === 0 ? <div className="text-muted">Нет данных</div> : (
              <Table size="sm" striped hover responsive>
                <thead><tr><th>Блюдо</th><th className="text-end">Кол-во</th><th className="text-end">Выручка</th></tr></thead>
                <tbody>
                  {topItems.map(i => (
                    <tr key={i._id}>
                      <td>{i._id}</td>
                      <td className="text-end">{i.qty}</td>
                      <td className="text-end">{number(i.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body></Card>
        </Col>
      </Row>
    </Container>
  );
};

export default OwnerStats;

